import { useEffect, useRef, useState } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { Loader2, Clock, Mail, Check } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { accessGateway, accessResend, isFanIdentified, fetchProject } from "@/lib/api";
import { useI18n } from "@/i18n";

// Stable FANLY access gateway. The email CTA points here (not at a raw magic token),
// so a click NEVER dead-ends: valid session or valid token → open the release;
// expired token → offer a fresh link while keeping the fan's existing release access.
export default function AccessGateway() {
  const { slug } = useParams();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ran = useRef(false);

  const [state, setState] = useState("checking"); // checking | expired | invalid
  const [info, setInfo] = useState({});
  const [releaseName, setReleaseName] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const token = params.get("token");

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      // Grab the release name for messaging (best-effort).
      fetchProject(slug).then((d) => setReleaseName(d?.project?.name || slug)).catch(() => setReleaseName(slug));

      if (!token) {
        // No token: an existing session opens directly; otherwise go to the release page.
        return navigate(`/${slug}`, { replace: true });
      }
      try {
        const res = await accessGateway(token);
        if (res.status === "authenticated") {
          return navigate(`/${slug}`, { replace: true });
        }
        // expired
        if (isFanIdentified()) {
          // Existing browser session → release access is intact, open directly.
          return navigate(`/${slug}`, { replace: true });
        }
        setInfo(res);
        setState("expired");
      } catch (e) {
        // Bad signature / invalid token. Still never lose access if a session exists.
        if (isFanIdentified()) return navigate(`/${slug}`, { replace: true });
        setState("invalid");
      }
    })();
  }, [slug, token, navigate]);

  const resend = async () => {
    if (!token) return;
    setSending(true);
    try {
      const r = await accessResend(token);
      setInfo((p) => ({ ...p, ...r }));
      setSent(true);
      toast.success(t("gw_resend_toast"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("gw_resend_error"));
    } finally {
      setSending(false);
    }
  };

  if (state === "checking") {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6" data-testid="gateway-loading">
        <Loader2 className="w-8 h-8 text-[#FF2A2A] animate-spin mb-6" />
        <p className="font-mono-x text-xs tracking-[0.3em] uppercase text-[#737373]">{t("gw_checking")}</p>
      </div>
    );
  }

  const name = releaseName || slug;

  if (state === "expired") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="min-h-[100dvh] flex flex-col items-center justify-center px-6 text-center"
        data-testid="gateway-expired"
      >
        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
             style={{ background: "#FF2A2A18", border: "1px solid #FF2A2A44" }}>
          <Clock className="w-6 h-6 text-[#FF2A2A]" />
        </div>
        <p className="font-mono-x text-[11px] tracking-[0.3em] uppercase text-[#FF2A2A] mb-3" data-testid="gateway-expired-title">
          {t("gw_expired_title")}
        </p>
        <h1 className="font-display text-2xl font-bold tracking-tight mb-3 text-white">
          {t("gw_expired_body").replace("{name}", name)}
        </h1>

        {sent ? (
          <div className="flex flex-col items-center mt-4" data-testid="gateway-sent">
            <div className="flex items-center gap-2 text-[#55E6B3] mb-2">
              <Check className="w-4 h-4" />
              <span className="font-mono-x text-[11px] tracking-widest uppercase">{t("gw_resend_toast")}</span>
            </div>
            <p className="text-[#a3a3a3] max-w-sm text-sm">
              {t("gw_resend_sent").replace("{email}", info.email_masked || "")}
            </p>
          </div>
        ) : (
          <>
            {info.email_masked && (
              <p className="text-[#737373] text-sm mb-6 flex items-center gap-2">
                <Mail className="w-4 h-4" /> {info.email_masked}
              </p>
            )}
            <button
              onClick={resend}
              disabled={sending}
              data-testid="gateway-resend"
              className="inline-flex items-center gap-2 bg-[#FF2A2A] text-white font-semibold rounded-full px-7 py-3.5 hover:scale-[1.03] transition-transform disabled:opacity-60"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {t("gw_resend_cta")}
            </button>
          </>
        )}
      </motion.div>
    );
  }

  // invalid
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="min-h-[100dvh] flex flex-col items-center justify-center px-6 text-center"
      data-testid="gateway-invalid"
    >
      <h1 className="font-display text-2xl font-bold tracking-tight mb-3 text-white">{t("gw_invalid_title")}</h1>
      <p className="text-[#a3a3a3] max-w-sm mb-8">{t("gw_invalid_body")}</p>
      <button
        onClick={() => navigate(`/${slug}`, { replace: true })}
        data-testid="gateway-open-release"
        className="inline-flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-3 hover:scale-105 transition-transform"
      >
        {t("gw_open_release").replace("{name}", name)}
      </button>
    </motion.div>
  );
}
